import React, { useRef, useEffect, useState } from 'react';
import { Canvas2DService } from '../services/Canvas2DService';
import { ProfilBaleinePersonnalise, OutilDessin } from '../types';
import './ModalEditeurProfil.css'; // À créer ou adapter du CSS de Traverses

interface ModalEditeurProfilProps {
  isOpen: boolean;
  onClose: () => void;
  onSauvegarder: (nom: string, profilData?: ProfilBaleinePersonnalise) => void;
  profilsPersonnalises: ProfilBaleinePersonnalise[];
  onChargerProfil: (profil: ProfilBaleinePersonnalise) => void;
  onSupprimerProfil: (id: string) => void;
}

export const ModalEditeurProfil: React.FC<ModalEditeurProfilProps> = ({
  isOpen,
  onClose,
  onSauvegarder,
  profilsPersonnalises,
  onChargerProfil,
  onSupprimerProfil
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const canvasServiceRef = useRef<Canvas2DService | null>(null);
  const [nomProfil, setNomProfil] = useState('');
  const [profilEnEdition, setProfilEnEdition] = useState<ProfilBaleinePersonnalise | null>(null);
  const [rechercheProfil, setRechercheProfil] = useState('');
  const [pageActuelle, setPageActuelle] = useState(1);
  const [profilsParPage] = useState(6);
  const [vueGrille, setVueGrille] = useState(true);
  const [outilActuel, setOutilActuel] = useState<OutilDessin>(OutilDessin.CRAYON);
  const [accrocheActive, setAccrocheActive] = useState<boolean>(true);

  useEffect(() => {
    if (isOpen && canvasRef.current && !canvasServiceRef.current) {
      try {
        canvasServiceRef.current = new Canvas2DService({
          largeur: 800,
          hauteur: 600,
          couleurFond: '#ffffff',
          couleurGrille: '#e0e0e0',
          couleurDessin: '#000000',
          couleurSelection: '#ff0000',
          epaisseurLigne: 2,
          taillePoint: 4,
          afficherGrille: true,
          magnetisme: true,
          toleranceSelection: 10
        });
        canvasServiceRef.current.initialiser(canvasRef.current);
      } catch (error) {
        console.error('Erreur lors de l\'initialisation du canvas 2D:', error);
      }
    }

    return () => {
      if (!isOpen && canvasServiceRef.current) {
        canvasServiceRef.current.dispose();
        canvasServiceRef.current = null;
      }
    };
  }, [isOpen]);

  useEffect(() => {
    setPageActuelle(1);
  }, [profilsPersonnalises.length]);

  const handleSauvegarder = () => {
    if (nomProfil.trim() && canvasServiceRef.current) {
      const profilData = canvasServiceRef.current.exporterMotif(nomProfil.trim());
      if (profilData) {
        if (profilEnEdition) {
          profilData.id = profilEnEdition.id;
        }
        onSauvegarder(nomProfil.trim(), profilData);
        setNomProfil('');
        setProfilEnEdition(null);
      }
    }
  };

  const supprimerSelection = () => {
    if (canvasServiceRef.current) {
      canvasServiceRef.current.supprimerSelection();
    }
  };

  const toggleAccroche = () => {
    if (canvasServiceRef.current) {
      canvasServiceRef.current.toggleAccroche();
      setAccrocheActive(canvasServiceRef.current.getAccrocheActive());
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  const profilsFiltres = profilsPersonnalises.filter(profil => 
    profil.nom.toLowerCase().includes(rechercheProfil.toLowerCase())
  );
  
  const indexDebut = (pageActuelle - 1) * profilsParPage;
  const indexFin = indexDebut + profilsParPage;
  const profilsAffiches = profilsFiltres.slice(indexDebut, indexFin);
  const nombrePages = Math.ceil(profilsFiltres.length / profilsParPage);

  const changerPage = (nouvellePage: number) => {
    setPageActuelle(Math.max(1, Math.min(nouvellePage, nombrePages)));
  };

  const changerOutil = (outil: OutilDessin) => {
    setOutilActuel(outil);
    if (canvasServiceRef.current) {
      canvasServiceRef.current.definirOutil(outil);
    }
  };

  

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-overlay" onClick={onClose} onKeyDown={handleKeyDown} tabIndex={0}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Éditeur de profil personnalisé</h3>
          <button className="modal-close" onClick={onClose}>
            ×
          </button>
        </div>
        
        <div className="modal-body">
          <div className="section-principale">
            <div className="canvas-container">
              <canvas
                ref={canvasRef}
                width={800}
                height={600}
                className="canvas-dessin"
              />
            </div>
            
            <div className="canvas-controles">
              <div className="outils-dessin">
                <button
                  onClick={() => changerOutil(OutilDessin.CRAYON)}
                  className={`bouton-canvas ${outilActuel === OutilDessin.CRAYON ? 'actif' : ''}`}
                  title="Outil crayon"
                >
                  ✏️ Crayon
                </button>
                <button
                  onClick={() => changerOutil(OutilDessin.LIGNE)}
                  className={`bouton-canvas ${outilActuel === OutilDessin.LIGNE ? 'actif' : ''}`}
                  title="Outil ligne droite"
                >
                  📏 Ligne
                </button>
                <button
                  onClick={() => changerOutil(OutilDessin.BEZIER)}
                  className={`bouton-canvas ${outilActuel === OutilDessin.BEZIER ? 'actif' : ''}`}
                  title="Outil courbe de Bézier"
                >
                  🎨 Bézier
                </button>
                <button
                  onClick={() => changerOutil(OutilDessin.SELECTION)}
                  className={`bouton-canvas ${outilActuel === OutilDessin.SELECTION ? 'actif' : ''}`}
                  title="Outil de sélection"
                >
                  👆 Sélection
                </button>
              </div>
              
              <div className="actions-canvas">
                <button
                  onClick={supprimerSelection}
                  className="bouton-canvas"
                  title="Supprimer la sélection"
                >
                  🗑️ Supprimer
                </button>
                <button
                  onClick={() => canvasServiceRef.current?.annuler()}
                  className="bouton-canvas"
                >
                  ↶ Annuler
                </button>
                <button
                  onClick={() => canvasServiceRef.current?.effacer()}
                  className="bouton-canvas"
                >
                  🧹 Effacer tout
                </button>
                <button
                  onClick={() => canvasServiceRef.current?.toggleGrille()}
                  className="bouton-canvas"
                >
                  ⊞ Grille
                </button>
                <button
                  onClick={toggleAccroche}
                  className={`bouton-canvas ${accrocheActive ? 'actif' : ''}`}
                  title="Accroche magnétique"
                >
                  🧲 Accroche
                </button>
              </div>
              
              <div className="sauvegarde-inline">
                <input
                  type="text"
                  value={nomProfil}
                  onChange={(e) => setNomProfil(e.target.value)}
                  placeholder="Nom du profil..."
                  className="input-nom-profil"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSauvegarder();
                    }
                  }}
                />
                <button
                  onClick={handleSauvegarder}
                  disabled={!nomProfil.trim()}
                  className="bouton-canvas bouton-primary"
                >
                  💾 Sauvegarder
                </button>
              </div>
            </div>
          </div>
          
          <div className="panneau-profils">
            <div className="gestion-header">
              <h4>Profils ({profilsFiltres.length})</h4>
              <div className="gestion-controles">
                <button
                  onClick={() => setVueGrille(!vueGrille)}
                  className="bouton-vue"
                  title={vueGrille ? 'Vue liste' : 'Vue grille'}
                >
                  {vueGrille ? '☰' : '⊞'}
                </button>
              </div>
            </div>
            
            <div className="recherche-section">
              <input
                type="text"
                value={rechercheProfil}
                onChange={(e) => {
                  setRechercheProfil(e.target.value);
                  setPageActuelle(1);
                }}
                placeholder="Rechercher un profil..."
                className="input-recherche"
              />
              {rechercheProfil && (
                <button
                  onClick={() => {
                    setRechercheProfil('');
                    setPageActuelle(1);
                  }}
                  className="bouton-effacer-recherche"
                >
                  ✕
                </button>
              )}
            </div>
            
            {profilsFiltres.length > 0 ? (
              <div className="profils-existants">
                <div className={`liste-profils-canvas ${vueGrille ? 'vue-grille' : 'vue-liste'}`}>
                  {profilsAffiches.map(profil => (
                    <div key={profil.id} className="profil-canvas-item">
                      {vueGrille && (
                        <div className="profil-miniature">
                          <img 
                            src={Canvas2DService.genererMiniature(profil, 120)} 
                            alt={`Miniature de ${profil.nom}`}
                            className="miniature-image"
                          />
                        </div>
                      )}
                      <div className="profil-info">
                        <span className="profil-nom" title={profil.nom}>{profil.nom}</span>
                        <span className="profil-details">
                          {profil.lignes.length} ligne{profil.lignes.length > 1 ? 's' : ''}
                        </span>
                      </div>
                      <div className="profil-actions">
                        <button
                          onClick={() => {
                            if (canvasServiceRef.current) {
                              canvasServiceRef.current.importerMotif(profil);
                            }
                            setNomProfil(profil.nom);
                            setProfilEnEdition(profil);
                          }}
                          className="bouton-motif bouton-editer"
                          title="Éditer ce profil"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => {
                            onChargerProfil(profil);
                            onClose();
                          }}
                          className="bouton-motif bouton-charger"
                          title="Utiliser ce profil"
                        >
                          ✓
                        </button>
                        <button
                          onClick={() => {
                            if (window.confirm(`Êtes-vous sûr de vouloir supprimer le profil "${profil.nom}" ?`)) {
                              onSupprimerProfil(profil.id);
                            }
                          }}
                          className="bouton-motif bouton-supprimer"
                          title="Supprimer ce profil"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                
                {nombrePages > 1 && (
                  <div className="pagination">
                    <button
                      onClick={() => changerPage(pageActuelle - 1)}
                      disabled={pageActuelle === 1}
                      className="bouton-page"
                    >
                      ‹
                    </button>
                    
                    <span className="info-page">
                      Page {pageActuelle} sur {nombrePages}
                    </span>
                    
                    <button
                      onClick={() => changerPage(pageActuelle + 1)}
                      disabled={pageActuelle === nombrePages}
                      className="bouton-page"
                    >
                      ›
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <p className="aucun-motif">Aucun profil personnalisé trouvé.</p>
            )}
          </div>
        </div>
      </div>
      </div>
    </>
  );
};